import React, { useMemo } from 'react';

import { Box, VStack } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { BlankScreen } from 'components/blank-screen';
import LoadingAnimation from 'components/loading-animation';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { useSupportData } from 'hooks/use-support-data';
import { DetailsModalWrapper } from 'pages/account/account-orders/details-modal/details-modal-wrapper';
import LoadingIndicator from 'pages/store-locator/loading-indicator';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useUIContext } from 'state/ui';
import { everyCartEntriesInMenu } from 'utils/cart';
import noop from 'utils/noop';

import { MODAL_TYPE } from '../constants';

import { CartEntry } from './cart-entry';
import {
  Block,
  Container,
  Divider,
  Footer,
  LoadingContainer,
  ModalHead,
  NeedHelpButtonLoadingIndicatorWrapper,
  OrderDetailsScrollContainer,
  StyledCartEntries,
  StyledHeading,
} from './details-modal.styled';
import IsEnRouteModal from './is-en-route-modal';
import { PointsEarnedBlock } from './points-earned';
import { ReceiptDetails } from './receipt-details';
import { ReceiptHeadInfo } from './receipt-head-info';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

const DetailsModal: React.FC<React.PropsWithChildren<{
  rbiOrderId: string;
  onModalDismiss: () => void;
  type: string;
  showTrackOrderModalPage?: () => void;
  setDetailsModal?: (orderId: string, type: MODAL_TYPE) => void;
}>> = ({ rbiOrderId, onModalDismiss, type, showTrackOrderModalPage, setDetailsModal = noop }) => {
  const { navigate } = useNavigation();
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const { order, loading: isOrderLoading, refetch } = useOrderDetails(rbiOrderId);

  const { handleReorderClick, disabled } = useReorder(order);
  const {
    reorder: { reordering },
  } = useOrderContext();
  const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);

  const { logNavigationClick } = useMParticleContext();
  const { isDeliveryActive } = useRefundEligibility({
    orderId: rbiOrderId,
  });
  const { isSupportDataLoading } = useSupportData();

  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);
  const disableReorder = useMemo(
    () => disabled || !everyCartEntriesInMenu(order?.cart?.reorderCartEntries || []),
    [disabled, order]
  );

  if (isDeliveryActive && type !== MODAL_TYPE.SHOW_DETAILS) {
    return <IsEnRouteModal rbiOrderId={rbiOrderId} onModalDismiss={() => onModalDismiss()} />;
  }

  const loyaltyTransaction = order?.loyaltyTransaction;
  const handleNeedHelpClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK_RECENT_ORDER_NEED_HELP);
    navigate(helpLink);
  };

  const handleTrackOrder = () => {
    if (showTrackOrderModalPage) {
      showTrackOrderModalPage();
    } else {
      setDetailsModal(order.rbiOrderId, MODAL_TYPE.TRACK_ORDER);
    }
  };

  // TODO: ReactNavigation GST-92 Move away from Modal component
  return (
    <DetailsModalWrapper
      onDismiss={() => onModalDismiss()}
      mParticleEventData={{
        modalAppearanceEventMessage: 'Order Details',
      }}
      header={
        <ModalHead>
          <StyledHeading>{formatMessage({ id: 'receipt' })}</StyledHeading>
        </ModalHead>
      }
      footer={
        <Footer>
          <Container>
            <VStack space="$3">
              {isDeliveryActive ? (
                <ActionButton fullWidth onPress={handleTrackOrder} isLoading={isOrderLoading}>
                  {formatMessage({ id: 'trackOrder' })}
                </ActionButton>
              ) : (
                <ActionButton
                  fullWidth
                  onPress={() => {
                    onModalDismiss();
                    handleReorderClick();
                  }}
                  isLoading={reordering || isOrderLoading}
                  disabled={disableReorder}
                >
                  {formatMessage({ id: 'reorder' })}
                </ActionButton>
              )}
              <Box focusable>
                {formatMessage({
                  id: 'pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder',
                })}
              </Box>

              {isSupportDataLoading && enableOrderSupport && (
                <NeedHelpButtonLoadingIndicatorWrapper>
                  <LoadingIndicator />
                </NeedHelpButtonLoadingIndicatorWrapper>
              )}

              {/*
               * isSupportDataLoading becomes false quickly, so aria-live="polite" on this button is distracting.
               * This will silently appear for screen readers.
               */}
              {!isSupportDataLoading && enableOrderSupport && (
                <Box>
                  <ActionButton
                    testID="recent-order-need-help"
                    fullWidth
                    variant={ActionButtonVariants.OUTLINE}
                    size={ActionButtonSizes.SMALL}
                    onPress={handleNeedHelpClick}
                  >
                    {formatMessage({ id: 'needHelp' })}
                  </ActionButton>
                </Box>
              )}
            </VStack>
          </Container>
        </Footer>
      }
    >
      {!isOrderLoading && order ? (
        <OrderDetailsScrollContainer>
          <Container>
            <Block>
              <ReceiptHeadInfo order={order} />
              <Divider />
            </Block>
            {loyaltyTransaction && (
              <>
                <PointsEarnedBlock lylTransaction={loyaltyTransaction} />
                <Divider />
              </>
            )}
            <StyledCartEntries>
              {order.cart.cartEntries.map((entry, i) => (
                <React.Fragment key={entry.name! + i}>
                  <CartEntry currencyFormatter={formatCurrencyForLocale} entry={entry} index={i} />
                  <Divider />
                </React.Fragment>
              ))}
            </StyledCartEntries>
            <Block>
              <ReceiptDetails order={order} />
            </Block>
          </Container>
        </OrderDetailsScrollContainer>
      ) : isOrderLoading ? (
        <LoadingContainer>
          <LoadingAnimation />
        </LoadingContainer>
      ) : (
        <BlankScreen context={{ reason: 'there is not rbiOrderId' }} onReload={refetch} />
      )}
    </DetailsModalWrapper>
  );
};

export default DetailsModal;
